import { buildProductArgs } from 'Utils/graphql';

function productFeatureServiceQuery(lineProps) {
  const { ageInformation, groupMin, groupMax, groupRange } = lineProps;
  return `
    hasBookingOnline
    hasBookingOnsite
    id
    isIdealForFamily
    label
    description
    detailedDescription
    durationDescription
    ageRestriction(message: "${ageInformation}")
    level
    note
    group(min: "${groupMin}" max: "${groupMax}" range: "${groupRange}")
    specificConditions
    equipments {
      label
      included
    }
    utilities {
      label
      included
    }
    targetPrice {
      price
      currency
    }
  `;
}

const productFeatureFacilityQuery = `
    id
    description
    distance
    label
    nightLighting
    onSite
    openingHours {
      opening
      closing
    }
    outdoor
    quantity
    specificInformation {
      type
      value
    }
    technicalSpecificities {
      typeDescription
      length
      width
      minDepth
      maxDepth
      unit
    }
    localisation
  `;

export function productWellnessDetailQuery(productId, activityId, { lineProps, urlParams }) {
  const productArgs = buildProductArgs(productId, urlParams);
  const activityArgs = `categoryId: "${activityId}"`;

  return `query {
      config {
        common { previous next }
      }
      includedServices: activities(${productArgs}, ${activityArgs}, onlyIncluded: true) {
        ${productFeatureServiceQuery(lineProps)}
      }
      onDemandServices: activities(${productArgs}, ${activityArgs}, onlyNotIncluded: true) {
        ${productFeatureServiceQuery(lineProps)}
      }
      productActivities(${productArgs}) {
        facilities(activityId : "${activityId}") {
          ${productFeatureFacilityQuery}
        }
      }
    }`;
}

const productActivityExcursionQuery = `
    seasonality
    transportation
    whatToBring
  `;

export function productActivityDetailQuery(productId, activityId, { lineProps, urlParams }) {
  const productArgs = buildProductArgs(productId, urlParams);
  const activityArgs = `categoryId: "${activityId}"`;

  return `query {
    config {
      common { previous next }
    }
    includedServices: activities(${productArgs}, ${activityArgs}, onlyIncluded: true) {
      ${productFeatureServiceQuery(lineProps)}
    }
    onDemandServices: activities(${productArgs}, ${activityArgs}, onlyNotIncluded: true) {
      ${productFeatureServiceQuery(lineProps)}
      ${productActivityExcursionQuery}
    }
    productActivities(${productArgs}) {
      facilities(activityId : "${activityId}") {
        ${productFeatureFacilityQuery}
      }
    }
  }`;
}

export function productChildcareDetailQuery(productId, activityId, { lineProps, urlParams }) {
  const productArgs = buildProductArgs(productId, urlParams);
  const productActivitiesArgs = `activityId : "${activityId}"`;
  const activityArgs = `categoryId: "${activityId}"`;
  const CHILDREN_ACTIVITY_IDENTIFIER = '_CHILD_';

  return `query {
    config {
      common { previous next }
    }
    resort(${productArgs}) {
      facilities(types: ["CHILDREN"], withEquipments: true) {
        subtitle
        label
        equipments {
          id
          label
        }
      }
      productActivities(idFilter: "${CHILDREN_ACTIVITY_IDENTIFIER}") {
        subtitle
        label
        equipments {
          id
          label
        }
      }
    }
    productActivities(${productArgs}) {
      childcare(${productActivitiesArgs}) {
        description
        duration
        furtherInformation
        id
        label
        openingDates {
          opening
          closing
        }
        philosophy
        targetPrice {
          currency
          price
        }
      }
      facilities(${productActivitiesArgs}) {
        ${productFeatureFacilityQuery}
      }
    }
    includedServices: activities(${productArgs}, ${activityArgs}, onlyIncluded: true) {
      ${productFeatureServiceQuery(lineProps)}
    }
    onDemandServices: activities(${productArgs}, ${activityArgs}, onlyNotIncluded: true) {
      ${productFeatureServiceQuery(lineProps)}
    }
  }`;
}

/* lists of features */
const productFeaturesConfigQuery = `
    config {
      common {
        allInclusive
        backNavigation
        bookOnlineOnly
        bookOnlineOrOnsite
        bookOnsiteOnly    
        childcarealacarte
        downloadLeafletLabel
        extraCost
        howToBookLabel
        idealForFamilyLabel
        included
        loadLess
        onDemandService
        philosophyLabel
        price {
          priceFrom
        }
        scrollBackward
        scrollForward
        treatmentsLabel
        viewMore
      }
    }
  `;

function productFeaturesCategoriesFields(defaultLabel) {
  const activityLabel = defaultLabel ? `label(defaultLabel: "${defaultLabel}")` : 'label';

  return `
    activities: subCategories {
      bookingDescription
      brandLogoUrl
      description
      equipmentRentalDescription
      id
      image: imageUrl
      isChildrenClub
      isIncluded
      keywords
      ${activityLabel}
      name
      numberOfIncludedActivities
      numberOfOnDemandActivities
      philosophy
      ranking
      supervisionDescription
      termsAndConditionsDescription
    }
    id
    label
    ranking
  `;
}

export function productActivitiesQuery({ productId, defaultLabel, urlParams }) {
  const productArgs = buildProductArgs(productId, urlParams);
  return `query {
    ${productFeaturesConfigQuery}
    resort(${productArgs}) {
      activitiesIntroduction {
        description
        shortDescription
      }
      categories: activityCategories {
        leisure {
          ${productFeaturesCategoriesFields(defaultLabel)}
        }
      }
    }
  }`;
}

export function productChildcaresQuery(productId, { urlParams }) {
  const productArgs = buildProductArgs(productId, urlParams);
  return `query {
    ${productFeaturesConfigQuery}
    resort(${productArgs}) {
      kidsActivitiesIntroduction {
        description
        shortDescription
      }
      categories: activityCategories {
        children {
          ${productFeaturesCategoriesFields()}
        }
      }
    }
  }`;
}

export function productWellnessQuery(productId, { urlParams }) {
  const productArgs = buildProductArgs(productId, urlParams);
  return `query {
    ${productFeaturesConfigQuery}
    resort(${productArgs}) {
      wellnessActivitiesIntroduction {
        bookletUrl
        description
        shortDescription
      }
      categories: activityCategories {
        wellness {
          ${productFeaturesCategoriesFields()}
        }
      }
    }
  }`;
}
